import React, { useEffect, useId, useRef, useState } from "react";

const MapHomeIconSvg = `<svg
    width="32"
    height="36"
    viewBox="0 0 32 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="4" fill="#0395FF" />
    <path d="M20 32H12L16 36L20 32Z" fill="#0395FF" />
    <mask
      id="mask0_450_4331"
      // style="mask-type:alpha"
      maskUnits="userSpaceOnUse"
      x="4"
      y="4"
      width="24"
      height="24"
    >
      <rect x="4" y="4" width="24" height="24" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_450_4331)">
      <path
        d="M8 25V15.625L6.2 17L5 15.4L8 13.1V10H10V11.575L16 7L27 15.4L25.8 16.975L24 15.625V25H8ZM10 23H15V19H17V23H22V14.1L16 9.525L10 14.1V23ZM8 9C8 8.16667 8.29167 7.45833 8.875 6.875C9.45833 6.29167 10.1667 6 11 6C11.2833 6 11.521 5.904 11.713 5.712C11.9043 5.52067 12 5.28333 12 5H14C14 5.83333 13.7083 6.54167 13.125 7.125C12.5417 7.70833 11.8333 8 11 8C10.7167 8 10.479 8.09567 10.287 8.287C10.0957 8.479 10 8.71667 10 9H8Z"
        fill="white"
      />
    </g>
  </svg>`;

// google.maps.OverlayView only exists after the Maps script loads, so the subclass
// is built on first use and cached instead of being redefined on every render.
let PopupOverlay = null;

const getPopupOverlay = () => {
  if (PopupOverlay) {
    return PopupOverlay;
  }

  PopupOverlay = class Popup extends window.google.maps.OverlayView {
    position;
    containerDiv;
    constructor(position, content, type = "center") {
      super();
      this.position = position;

      if (type === "points") {
        content.classList.add("popup-bubble");

        // This zero-height div is positioned at the bottom of the bubble.
        const bubbleAnchor = document.createElement("div");

        bubbleAnchor.classList.add("popup-bubble-anchor");
        bubbleAnchor.appendChild(content);

        // This zero-height div is positioned at the bottom of the tip.
        this.containerDiv = document.createElement("div");
        this.containerDiv.classList.add("popup-container");
        this.containerDiv.appendChild(bubbleAnchor);

        // Optionally stop clicks, etc., from bubbling up to the map.
        PopupOverlay.preventMapHitsAndGesturesFrom(this.containerDiv);
      } else {
        content.classList.add("popup-center-bubble");

        this.containerDiv = document.createElement("div");
        this.containerDiv.classList.add("popup-center-container");
        this.containerDiv.appendChild(content);

        PopupOverlay.preventMapHitsAndGesturesFrom(this.containerDiv);
      }
    }

    /** Called when the popup is added to the map. */
    onAdd() {
      this.getPanes().floatPane.appendChild(this.containerDiv);
    }

    /** Called when the popup is removed from the map. */
    onRemove() {
      if (this.containerDiv.parentElement) {
        this.containerDiv.parentElement.removeChild(this.containerDiv);
      }
    }

    /** Called each frame when the popup needs to draw itself. */
    draw() {
      const divPosition = this.getProjection().fromLatLngToDivPixel(
        this.position
      );
      if (!divPosition) return;

      // Hide the popup when it is far out of view.
      const display =
        Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000
          ? "block"
          : "none";

      if (display === "block") {
        this.containerDiv.style.left = divPosition.x + "px";
        this.containerDiv.style.top = divPosition.y + "px";
      }

      if (this.containerDiv.style.display !== display) {
        this.containerDiv.style.display = display;
      }
    }
  };

  return PopupOverlay;
};

const MyMapComponent = ({
  center,
  zoom,
  className,
  coordinates,
  streetView = false,
  mapTypeControl = false,
  zoomControl = false,
  fullscreenControl = false,
}) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const [map, setMap] = useState();
  const mapId = useId();
  const centerLat = parseFloat(center?.lat);
  const centerLng = parseFloat(center?.lng);

  const storeMapInstance = (instance) => {
    mapRef.current = instance;
    setMap(instance);
  };

  // Initialize once per coordinate; do not tear down when map state updates (that caused Street View flashing).
  useEffect(() => {
    if (!ref.current || Number.isNaN(centerLat) || Number.isNaN(centerLng)) {
      return undefined;
    }

    let cancelled = false;
    const mapCenter = { lat: centerLat, lng: centerLng };

    if (streetView) {
      new window.google.maps.StreetViewService().getPanorama(
        { location: mapCenter, radius: 50 },
        function (data, status) {
          if (cancelled || !ref.current) {
            return;
          }

          if (status === window.google.maps.StreetViewStatus.OK) {
            const paronama = new window.google.maps.StreetViewPanorama(
              ref.current,
              {
                position: mapCenter,
                pov: {
                  heading: 0,
                  pitch: 0,
                },
              }
            );
            const whereToLookLatLng = new window.google.maps.LatLng(
              centerLat,
              centerLng
            );
            const manLatLng = data.location.latLng;
            const heading =
              window.google.maps.geometry.spherical.computeHeading(
                manLatLng,
                whereToLookLatLng
              );
            const pov = paronama.getPov();
            pov.heading = heading;
            pov.zoom = 0;
            paronama.setPov(pov);
            storeMapInstance(paronama);
          } else {
            storeMapInstance(
              new window.google.maps.Map(ref.current, {
                center: mapCenter,
                zoom: zoom,
                mapTypeControl: mapTypeControl,
                zoomControl: zoomControl,
                streetViewControl: streetView,
                fullscreenControl: fullscreenControl,
              })
            );
          }
        }
      );
    } else {
      storeMapInstance(
        new window.google.maps.Map(ref.current, {
          center: mapCenter,
          zoom: zoom,
          mapTypeControl: mapTypeControl,
          zoomControl: zoomControl,
          streetViewControl: streetView,
          fullscreenControl: fullscreenControl,
        })
      );
    }

    return () => {
      cancelled = true;
      mapRef.current = null;
      setMap(undefined);
    };
  }, [
    centerLat,
    centerLng,
    zoom,
    streetView,
    mapTypeControl,
    zoomControl,
    fullscreenControl,
  ]);

  // useEffect for showing popup of coordinates and set zoom level based on coordinates
  useEffect(() => {
    if (!map || !coordinates?.length) {
      return undefined;
    }

    const Popup = getPopupOverlay();
    const bounds = new window.google.maps.LatLngBounds();
    const popups = [];

    coordinates.forEach((add) => {
      const lat = parseFloat(add?.lat);
      const lng = parseFloat(add?.lng);
      if (lat === centerLat || lng === centerLng) {
        return;
      }

      const tag = document.createElement("div");
      tag.appendChild(document.createTextNode(add.address));

      bounds.extend(new window.google.maps.LatLng(lat, lng));

      const popup = new Popup(
        new window.google.maps.LatLng(lat, lng),
        tag,
        "points"
      );
      popup.setMap(map);
      popups.push(popup);
    });

    bounds.extend(new window.google.maps.LatLng(centerLat, centerLng));
    // Street View panoramas share this state slot but have no fitBounds.
    if (typeof map.fitBounds === "function") {
      map.fitBounds(bounds, 50);
    }

    return () => {
      popups.forEach((popup) => popup.setMap(null));
    };
  }, [map, coordinates, centerLat, centerLng]);

  // useEffect for centering showing center marker
  useEffect(() => {
    if (!map || Number.isNaN(centerLat) || Number.isNaN(centerLng)) {
      return undefined;
    }

    const Popup = getPopupOverlay();
    const tag = document.createElement("span");
    tag.insertAdjacentHTML("beforeend", MapHomeIconSvg);

    const popup = new Popup(
      new window.google.maps.LatLng(centerLat, centerLng),
      tag,
      "center"
    );
    popup.setMap(map);

    return () => {
      popup.setMap(null);
    };
  }, [map, centerLat, centerLng]);

  return (
    <>
      <div className={className + " map-container"} id={mapId} ref={ref} />
    </>
  );
};

export default MyMapComponent;
